import {useMutation} from '@tanstack/react-query';
import {useNavigate} from 'react-router';

import {useFeatureFlagsApi} from 'apps/feature-flags/src/api/feature-flags';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useTranslation} from 'common/i18n/hooks';
import {getReportUrl} from 'common/report/util';
import {useFeatureFlagStore} from 'apps/feature-flags/src/store';
import type {FeatureFlagApiPayload, FeatureFlagApiResponse} from 'app_api/projects/feature_flags/types';
import {
  ListPageTrackingEvent,
  trackFeatureFlagListPageAction,
  TrackingEventSource,
} from 'apps/feature-flags/src/tracking';
import {useContentSidenavContext} from 'common/widgets/content-sidenav/context';
import {SidenavSectionIndex, SidenavSectionTrackingName} from 'common/widgets/content-sidenav/constants';
import {ContentItemInfo} from 'common/widgets/content-sidenav/types';

import {useFeatureFlagPageContext} from '../context';

type UseCreateFeatureFlagProps = {
  projectId: number;
};

export function useCreateFeatureFlag(props?: UseCreateFeatureFlagProps) {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const contentSidenavContext = useContentSidenavContext();
  let projectId: number;
  try {
    projectId = useFeatureFlagPageContext().projectId;
  } catch (_) {
    projectId = 0;
  }
  if (props?.projectId) {
    projectId = props.projectId;
  }

  const {createFeatureFlag} = useFeatureFlagsApi({projectId});
  const setEditModalOpen = useFeatureFlagStore((store) => store.setEditModalOpen);

  const trackFeatureFlagCreationAttempt = (isSuccess: boolean, featureFlag?: FeatureFlagApiResponse) => {
    trackFeatureFlagListPageAction({
      event: ListPageTrackingEvent.CreateFeatureFlag,
      source: TrackingEventSource.ListPageHeader,
      featureFlag,
      props: {
        isSuccess,
      },
    });
  };

  const createMutation = useMutation({
    mutationFn: createFeatureFlag,
    onSuccess: (response) => {
      trackFeatureFlagCreationAttempt(true, response);
      setEditModalOpen(true);
      contentSidenavContext?.insertContentSidenavItem(
        response as ContentItemInfo,
        SidenavSectionIndex.Yours,
        SidenavSectionTrackingName.Yours,
      );
      navigate(getReportUrl(`feature-flags/${response.id}`, {projectId}));
    },
    onError: () => {
      trackFeatureFlagCreationAttempt(false);
      addToast({
        message: i18n.t(`feature-flags.create.error`),
        type: ToastType.ERROR,
        timeout: 6000,
      });
    },
  });

  return (newFeatureFlag: FeatureFlagApiPayload) => {
    createMutation.mutate(newFeatureFlag);
  };
}
